/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

export
class ImageDataTile {
    public _parser: ImageDataParser;
    public width: number = 0;
    public height: number = 0;
    public compressedUint8Data: Uint8Array;
    public data: Uint8Array = null;

    public ready = false;

    constructor(aImageDataParser: ImageDataParser, aXIndex: number, aYIndex: number, aUint8Data: Uint8Array) {
        this._parser = aImageDataParser;
        this.compressedUint8Data = aUint8Data;
    }

    public cleanupLoadedData() {
        this.data = null;
        this.ready = false;
    }
}

export default
class ImageDataParser {

    private textureProperties: any;
    private valid: boolean = false;

    private headerSize: number = 0;
    private tileResolution: number = 0;
    private rows: number = 1;
    private cols: number = 1;

    private tilesSizes: number[] = [];

    private width: number = -1;
    private height: number = -1;

    constructor(aTextureProperties: any) {
        this.textureProperties = aTextureProperties;
    }

    public getCols(): number {
        return this.cols;
    }

    public getRows(): number {
        return this.rows;
    }

    public parseTiledDataHeader(aArrayBuffer: ArrayBuffer) {
        //     Header format:
        //     (4B) header_size including_itself
        //     (4B) tile_resolution
        //     (4B) num_rows
        //     (4B) num_cols

        //     (4B) tile_1_size
        //     (4B) tile_2_size
        //      ..
        //      ..
        //      .. tile_(num_rows * num_cols)_size

        let textureOffsetInTextureBatch = this.textureProperties.offset;

        let headerReader = new DataView(aArrayBuffer, textureOffsetInTextureBatch);

        let pos: number = 0;
        this.headerSize = headerReader.getInt32(pos, true); pos += 4;
        this.tileResolution = headerReader.getInt32(pos, true); pos += 4;
        this.rows = headerReader.getInt32(pos, true); pos += 4;
        this.cols = headerReader.getInt32(pos, true); pos += 4;

        for (let i = 0; i < this.rows * this.cols; i++) {
            let value = headerReader.getInt32(pos, true); pos += 4;
            this.tilesSizes.push(value);
        }

        headerReader = null;
    }

    public loadTiles(aArrayBuffer: ArrayBuffer): ImageDataTile[] {
        let tiles = [];

        // skip header
        let pos = this.headerSize;

        let textureOffsetInTextureBatch = this.textureProperties.offset;

        let sizeIndex = 0;
        for (let row = 0; row !== this.rows; row++) {
            for (let col = 0; col !== this.cols; col++) {
                sizeIndex = (row * this.cols) + col;
                let uint8data = new Uint8Array(aArrayBuffer, textureOffsetInTextureBatch + pos, this.tilesSizes[sizeIndex]);
                tiles.push(new ImageDataTile(this, col, row, uint8data));
                pos += this.tilesSizes[sizeIndex];
            }
        }

        return tiles;
    }

    public parse(aArrayBuffer: ArrayBuffer) {
        this.width = this.textureProperties.width;
        this.height = this.textureProperties.height;

        // reset the content of tiles
        let tiles = [];

        if (this.textureProperties.tiled) {
            // parse the tiled texture data
            this.parseTiledDataHeader(aArrayBuffer);
            tiles = this.loadTiles(aArrayBuffer);
        }
        else { // regular not tiled texture data
            let uint8data;
            // if we do not know the size of the texture binary data or if the size is not defined
            if (this.textureProperties.size === 0) {
                uint8data = new Uint8Array(aArrayBuffer, this.textureProperties.offset);
            }
            else {
                uint8data = new Uint8Array(aArrayBuffer, this.textureProperties.offset, this.textureProperties.size);
            }
            tiles.push(new ImageDataTile(this, 0, 0, uint8data));
        }

        if (this.width >= 0 && this.height >= 0) {
            this.valid = true;
        }

        return tiles;
    }

    public isValid(): boolean {
        return this.valid;
    }
}